package gr.tech.site.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.site.article.ARTICLE_ENTRIES
import gr.tech.site.components.layouts.PageLayoutData
import gr.tech.site.models.ArticleEntry
import gr.tech.site.styles.BlogCardHoverStyle
import gr.tech.site.styles.Theme
import gr.tech.site.styles.wideCappedWidth
import gr.tech.site.util.ImageRepo
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

@InitRoute
fun initArticlesPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(
        showContactHeader = true,
        title = "Articles | Energy Rebate Guides for Growers",
        description = "In-depth guides on energy rebates, utility incentives, and commercial programs for indoor growers. Learn how to maximize every dollar back.",
        canonicalUrl = "https://greenrebates.com/article"
    ))
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun ArticlesPage() {
    Column(
        modifier = Modifier
            .wideCappedWidth()
            .classNames("mt-5", "mx-auto", "mb-5"),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        H1(
            Modifier.classNames("fs-1", "fw-bold", "w-full", "text-center", "mb-2")
                .toAttrs()
        ) {
            Text("Articles")
        }

        P(
            Modifier.classNames("fs-5", "text-center", "mb-4", "w-75")
                .color(Theme.DarkGreen.rgb)
                .opacity(0.8)
                .toAttrs()
        ) {
            Text("Practical guides on energy rebates, LED lighting, HVAC incentives, and utility programs for commercial growers.")
        }

        HorizontalDivider(modifier = Modifier.classNames("my-4"))

        Ul(
            attrs = Modifier
                .fillMaxWidth()
                .classNames("row", "row-cols-1", "row-cols-md-2", "row-cols-lg-3", "g-4", "list-unstyled")
                .toAttrs()
        ) {
            ARTICLE_ENTRIES.forEach { entry ->
                Li(attrs = Modifier.classNames("col").toAttrs()) {
                    ArticlePreviewCard(entry)
                }
            }
        }
    }
}

@Composable
fun ArticlePreviewCard(
    entry: ArticleEntry,
    showShadow: Boolean = true
) {
    Link(
        path = SitePage.Article.entryRoute(entry.routeOverride),
        openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
        modifier = Modifier
            .fillMaxWidth()
            .textDecorationLine(TextDecorationLine.None)
    ) {
        Div(
            attrs = BlogCardHoverStyle.toModifier()
                .then(
                    Modifier
                        .fillMaxWidth()
                        .classNames("card", "h-100")
                        .then( other =
                            if (showShadow)
                                Modifier.classNames("shadow")
                            else
                                Modifier
                        )
                        .cursor(Cursor.Pointer)
                        .border(1.px, LineStyle.Solid, Theme.MediumGreen.rgb)
                        .borderRadius(8.px)
                        .overflow(Overflow.Hidden)
                )
                .toAttrs {}
        ) {
            // Image
            Img(
                src = entry.image.ifEmpty { ImageRepo.MoneyBackground.src },
                alt = entry.title,
                attrs = Modifier
                    .fillMaxWidth()
                    .height(180.px)
                    .objectFit(ObjectFit.Cover)
                    .display(DisplayStyle.Block)
                    .toAttrs()
            )

            // Card body
            Div(attrs = Modifier.classNames("card-body", "d-flex", "flex-column").toAttrs()) {
                H2(
                    Modifier
                        .classNames("fs-5", "fw-bold", "mb-1")
                        .color(Theme.DarkGreen.rgb)
                        .toAttrs()
                ) {
                    Text(entry.title)
                }

                P(
                    Modifier
                        .classNames("fs-6", "lh-base", "mb-0", "flex-grow-1")
                        .color(Theme.DarkGreen.rgb)
                        .opacity(0.75)
                        .toAttrs()
                ) {
                    Text(entry.desc)
                }
            }

            // Card footer
            Div(
                attrs = Modifier
                    .classNames("card-footer", "d-flex", "justify-content-between", "align-items-center")
                    .toAttrs()
            ) {
                Span(
                    Modifier
                        .classNames("fs-6", "fw-medium", "text-truncate", "me-2")
                        .color(Theme.MediumGreen.rgb)
                        .toAttrs()
                ) {
                    Text(entry.author)
                }

                Span(
                    Modifier
                        .classNames("fs-6", "text-nowrap", "d-none", "d-sm-inline")
                        .color(Theme.DarkGreen.rgb)
                        .opacity(0.6)
                        .toAttrs()
                ) {
                    Text(entry.date)
                }
            }
        }
    }
}
